import { useState, useEffect, useRef } from "react";
import { useMasters } from "../../contexts/MastersContext";
import { useDesignation } from "../../contexts/DesignationsContext";
import { useEmploymentType } from "../../contexts/EmploymentTypeMasterContext";
import { interviewService } from "../../services/interviewService";
import {
  User,
  IndianRupee,
  Building2,
  FileText,
  MapPin,
  ChevronLeft,
  ChevronRight,
  Plus,
  X,
  AlertCircle,
  Calculator,
} from "lucide-react";
import { payrollService } from "../../services/payrollService";
import {
  calculateSalarySummary,
  calculateDynamicSalary,
} from "../../utils/salaryEngine";

const EmployeeForm = ({
  initialValues = {},
  onSubmit,
  onCancel,
  loading = false,
}) => {
  const { departments } = useMasters();
  const { designations } = useDesignation();
  const { employmentTypes } = useEmploymentType();

  const [activeTab, setActiveTab] = useState("personal");
  const submitButtonClicked = useRef(false);

  // Reporting to search states
  const [reportingToSearchQuery, setReportingToSearchQuery] = useState("");
  const [reportingToSearchResults, setReportingToSearchResults] = useState([]);
  const [showReportingToDropdown, setShowReportingToDropdown] = useState(false);
  const [isSearchingReportingTo, setIsSearchingReportingTo] = useState(false);
  const reportingToDropdownRef = useRef(null);
  const reportingToSearchTimeoutRef = useRef(null);

  const [salaryConfigurations, setSalaryConfigurations] = useState(null);
  const [salaryApplied, setSalaryApplied] = useState(false);

  // Fetch salary configurations when form opens
  useEffect(() => {
    const fetchConfigs = async () => {
      try {
        const response = await payrollService.getSalaryConfigurations();
        setSalaryConfigurations(response.data || response);
      } catch (error) {
        console.error("Error fetching salary configurations:", error);
      }
    };
    fetchConfigs();
  }, []);

  const [formData, setFormData] = useState({
    // Personal
    first_name: "",
    middle_name: "",
    last_name: "",
    email: "",
    phone: "",
    date_of_birth: "",
    date_of_joining: "",
    department_id: "",
    designation_id: "",
    reporting_to: "",
    employment_type_id: "",
    gender: "",
    status: "Active",
    // Salary
    ctc: "",
    basic_salary: "",
    hra: "",
    transport_allowance: "",
    medical_allowance: "",
    special_allowance: "",
    other_allowances: "",
    pf_enabled: false,
    pf_employee: "",
    pf_employer: "",
    esi_enabled: false,
    esi_employee: "",
    esi_employer: "",
    professional_tax: "200",
    dearness_allowance: "",
    bonus: "",
    gratuity: "",
    income_tax: "",
    lta: "",
    // Bank
    account_holder_name: "",
    account_number: "",
    bank_name: "",
    ifsc_code: "",
    branch_name: "",
    account_type: "Savings",
    // Statutory
    pan_number: "",
    aadhar_number: "",
    uan_number: "",
    esi_number: "",
    // Address
    current_address: "",
    permanent_address: "",
    city: "",
    state: "",
    pin_code: "",
    ...initialValues,
  });

  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState(null);

  // Update form data when initialValues change
  useEffect(() => {
    if (initialValues && Object.keys(initialValues).length > 0) {
      const pfEmployeeVal = initialValues.pf_employee ?? "";
      const pfEmployerVal = initialValues.pf_employer ?? "";
      const esiEmployeeVal = initialValues.esi_employee ?? "";
      const esiEmployerVal = initialValues.esi_employer ?? "";
      const computedPfEnabled =
        (parseFloat(pfEmployeeVal) || 0) > 0 ||
        (parseFloat(pfEmployerVal) || 0) > 0 ||
        !!initialValues.uan_number;
      const computedEsiEnabled =
        (parseFloat(esiEmployeeVal) || 0) > 0 ||
        (parseFloat(esiEmployerVal) || 0) > 0 ||
        !!initialValues.esi_number;

      setSalaryApplied(false);
      setFormData((prev) => {
        const salaryBreakdown = initialValues.salary_breakdown || {};
        const newFormData = {
          ...prev,
          ...initialValues,
          ...salaryBreakdown,
          // Ensure manual overrides for keys that might differ between backend and frontend
          department_id:
            initialValues.department_id || initialValues.department || "",
          designation_id:
            initialValues.designation_id || initialValues.designation || "",
          employment_type_id:
            initialValues.employment_type_id ||
            initialValues.employment_type ||
            "",
          pf_enabled: initialValues.pf_enabled ?? computedPfEnabled,
          esi_enabled: initialValues.esi_enabled ?? computedEsiEnabled,
          gender: initialValues.gender || "",
        };

        // Compatibility mapping for Edit mode initialization
        const legacyMappings = {
          basic_salary: ["basic", "basic_da"],
          hra: ["hr", "hra"],
          transport_allowance: ["coy", "transport"],
          bonus: ["bo", "bonus"],
          special_allowance: ["sp_all", "s_all", "special"],
          professional_tax: ["pt"],
          dearness_allowance: ["da"],
        };

        Object.entries(legacyMappings).forEach(([legacy, dynamics]) => {
          if (
            !newFormData[legacy] ||
            newFormData[legacy] === "0" ||
            newFormData[legacy] === 0
          ) {
            const foundVal = dynamics.find((d) => salaryBreakdown[d]);
            if (foundVal) {
              newFormData[legacy] = salaryBreakdown[foundVal];
            }
          }
        });

        return newFormData;
      });

      // Set reporting to search query and ID if they exist
      if (
        initialValues.reporting_to ||
        initialValues.manager ||
        initialValues.reporting_to_name
      ) {
        const reportingToValue = initialValues.reporting_to;
        const managerObj = initialValues.manager;
        const reportingToName = initialValues.reporting_to_name;

        let foundId = "";
        let foundName = "";

        // Case 1: reporting_to is an object
        if (typeof reportingToValue === "object" && reportingToValue !== null) {
          foundId =
            reportingToValue.employee_id ||
            reportingToValue.id ||
            reportingToValue.user_id;
          foundName =
            reportingToValue.full_name ||
            reportingToValue.name ||
            `${reportingToValue.first_name || ""} ${reportingToValue.last_name || ""}`.trim();
        }
        // Case 2: manager field exists (often found in linked data)
        else if (typeof managerObj === "object" && managerObj !== null) {
          foundId =
            managerObj.employee_id || managerObj.id || managerObj.user_id;
          foundName =
            managerObj.full_name ||
            managerObj.name ||
            `${managerObj.first_name || ""} ${managerObj.last_name || ""}`.trim();
        }
        // Case 3: reporting_to is a string
        else if (typeof reportingToValue === "string") {
          const isId =
            /^\d+$/.test(reportingToValue) ||
            reportingToValue.startsWith("EMP");
          if (isId) {
            foundId = reportingToValue;
          } else {
            foundName = reportingToValue;
          }
        }

        // Always fallback to reporting_to_name if foundName is still empty
        if (!foundName && reportingToName) {
          foundName = reportingToName;
        }

        // If we have an ID, set it and fetch the name if needed
        if (foundId) {
          setFormData((prev) => ({ ...prev, reporting_to: String(foundId) }));
          if (foundName) {
            setReportingToSearchQuery(foundName);
          } else {
            // Fetch name for the ID
            const resolveId = async () => {
              try {
                const response = await interviewService.searchInterviewers(
                  String(foundId),
                );
                if (
                  response.success &&
                  response.data &&
                  response.data.length > 0
                ) {
                  const person =
                    response.data.find(
                      (p) =>
                        String(p.employee_id || p.id || p.user_id) ===
                        String(foundId),
                    ) || response.data[0];
                  setReportingToSearchQuery(
                    person.full_name || person.name || String(foundId),
                  );
                } else {
                  setReportingToSearchQuery(String(foundId));
                }
              } catch (e) {
                setReportingToSearchQuery(String(foundId));
              }
            };
            resolveId();
          }
        }
        // If we ONLY have a name, search for the ID to prevent sending a name string
        else if (foundName) {
          setReportingToSearchQuery(foundName);
          // Crucially: Clear reporting_to in formData until it's resolved to an ID
          setFormData((prev) => ({ ...prev, reporting_to: "" }));

          const resolveName = async () => {
            try {
              const response =
                await interviewService.searchInterviewers(foundName);
              if (
                response.success &&
                response.data &&
                response.data.length > 0
              ) {
                // Try to find an exact name match
                const person =
                  response.data.find(
                    (p) =>
                      (
                        p.full_name ||
                        p.name ||
                        `${p.first_name} ${p.last_name}`
                      ).toLowerCase() === foundName.toLowerCase(),
                  ) || response.data[0];

                const resolvedId =
                  person.employee_id || person.id || person.user_id;
                if (resolvedId) {
                  setFormData((prev) => ({
                    ...prev,
                    reporting_to: String(resolvedId),
                  }));
                }
              }
            } catch (e) {
              console.error("Failed to auto-resolve manager name to ID", e);
            }
          };
          resolveName();
        }
      }
    }
  }, [initialValues]);

  const calculateSalarySummary = () => {
    // Determine dynamic structure for grouping if configuration exists
    let earningsTotal = 0;
    let deductionsTotal = 0;
    let contributionsTotal = 0;

    const monthlyCTC_Val = (parseFloat(formData.ctc) || 0) / 12;
    const structureId = monthlyCTC_Val < 30000 ? "below_30k" : "above_30k";

    // Use codes from dynamic config if available, otherwise fallback to hardcoded list
    const earningCodes = salaryConfigurations
      ? salaryConfigurations.salary_components
          .filter((c) => c.type === "earning" && c.structure_id === structureId)
          .map((c) => c.code)
      : [
          "basic_salary",
          "dearness_allowance",
          "hra",
          "transport_allowance",
          "medical_allowance",
          "special_allowance",
          "other_allowances",
          "bonus",
          "lta",
        ];

    earningCodes.forEach((code) => {
      earningsTotal += parseFloat(formData[code]) || 0;
    });

    const pfEnabled = !!formData.pf_enabled;
    const esiEnabled = !!formData.esi_enabled;

    const pfEmployee = pfEnabled ? parseFloat(formData.pf_employee) || 0 : 0;
    const pfEmployer = pfEnabled ? parseFloat(formData.pf_employer) || 0 : 0;
    const esiEmployee = esiEnabled ? parseFloat(formData.esi_employee) || 0 : 0;
    const esiEmployer = esiEnabled ? parseFloat(formData.esi_employer) || 0 : 0;
    const professionalTax = parseFloat(formData.professional_tax) || 0;
    const incomeTax = parseFloat(formData.income_tax) || 0;
    const gratuity = parseFloat(formData.gratuity) || 0;

    deductionsTotal = pfEmployee + esiEmployee + professionalTax + incomeTax;
    contributionsTotal = pfEmployer + esiEmployer + gratuity;

    const grossSalary = earningsTotal;
    const totalDeductions = deductionsTotal;
    const netSalary = grossSalary - totalDeductions;
    const monthlyCTC = grossSalary + contributionsTotal;
    const annualCTC = monthlyCTC * 12;

    return {
      grossSalary,
      totalDeductions,
      netSalary,
      annualCTC,
      monthlyCTC,
      employerContributions: contributionsTotal,
      earningCodes, // for UI rendering
    };
  };

  const salarySummary = calculateSalarySummary();

  // Auto-calculate PF/ESI and maintain CTC consistency
  useEffect(() => {
    const basic = parseFloat(formData.basic_salary) || 0;
    const da = parseFloat(formData.dearness_allowance) || 0;
    const ctcVal = parseFloat(formData.ctc) || 0;
    const monthlyCTC = ctcVal / 12;

    // Determine the structure rules based on threshold
    const isBelowThreshold = monthlyCTC < 30000;

    // PF logic based on structure
    let expectedPfEmp = "0";
    let expectedPfExp = "0";

    if (formData.pf_enabled) {
      if (isBelowThreshold) {
        // Below 30k rule for PF
        // (Gross - HRA) is usually used, but here we follow the handleApplyCTC logic for consistency
        expectedPfEmp = "1800.00";
        expectedPfExp = "1950.00";
      } else {
        // Above 30k rule for PF
        expectedPfEmp = "1800.00";
        expectedPfExp = "1950.00";
      }
    }

    // ESIC logic
    const esiEnabled = !!formData.esi_enabled;
    let expectedEsiEmp = "0";
    let expectedEsiExp = "0";

    const hra = parseFloat(formData.hra) || 0;
    const transport = parseFloat(formData.transport_allowance) || 0;
    const medical = parseFloat(formData.medical_allowance) || 0;
    const other = parseFloat(formData.other_allowances) || 0;
    const bonus = parseFloat(formData.bonus) || 0;
    const lta = parseFloat(formData.lta) || 0;
    const specialVal = parseFloat(formData.special_allowance) || 0;
    const gratuity = parseFloat(formData.gratuity) || 0;

    // Use engine for calculations where possible
    const tempGrossForEsic =
      basic + da + hra + transport + medical + other + bonus + lta + specialVal;

    if (esiEnabled) {
      if (tempGrossForEsic < 21000) {
        expectedEsiEmp = (tempGrossForEsic * 0.0075).toFixed(2);
        expectedEsiExp = (tempGrossForEsic * 0.0325).toFixed(2);
      } else {
        expectedEsiEmp = "0.00";
        expectedEsiExp = "0.00";
      }
    }

    // Calculate actual Gross for PT eligibility
    const currentGross =
      basic + da + hra + transport + medical + other + bonus + lta + specialVal;

    let updates = {};
    let shouldUpdate = false;

    // Update PF if inconsistent
    if (
      parseFloat(formData.pf_employee || 0) !== parseFloat(expectedPfEmp) ||
      parseFloat(formData.pf_employer || 0) !== parseFloat(expectedPfExp)
    ) {
      updates.pf_employee = expectedPfEmp;
      updates.pf_employer = expectedPfExp;
      shouldUpdate = true;
    }

    // Update ESI if inconsistent
    if (
      parseFloat(formData.esi_employee || 0) !== parseFloat(expectedEsiEmp) ||
      parseFloat(formData.esi_employer || 0) !== parseFloat(expectedEsiExp)
    ) {
      updates.esi_employee = expectedEsiEmp;
      updates.esi_employer = expectedEsiExp;
      shouldUpdate = true;
    }

    // Maintain CTC consistency by adjusting special allowance
    if (ctcVal > 0) {
      const pfEmployerVal =
        parseFloat(updates.pf_employer ?? formData.pf_employer) || 0;
      const esiEmployerVal =
        parseFloat(updates.esi_employer ?? formData.esi_employer) || 0;
      const gratuityVal =
        parseFloat(updates.gratuity ?? formData.gratuity) || 0;

      // Professional Tax logic (including Female logic based on Gross)
      const currentMonth = new Date().getMonth(); // 1 = February
      const isFeb = currentMonth === 1;
      let pt = 0;

      if (formData.gender === "Male") {
        pt = isFeb ? 300 : 200;
      } else if (formData.gender === "Female") {
        if (currentGross > 25000) {
          pt = isFeb ? 300 : 200;
        } else {
          pt = 0;
        }
      }

      if (parseFloat(formData.professional_tax || 0) !== pt) {
        updates.professional_tax = pt.toString();
        shouldUpdate = true;
      }

      // Income Tax logic
      let it = 0;
      if (ctcVal > 1200000) {
        it = (ctcVal * 0.1) / 12;
      }
      if (parseFloat(formData.income_tax || 0) !== parseFloat(it.toFixed(2))) {
        updates.income_tax = it.toFixed(2);
        shouldUpdate = true;
      }

      // Balancing formula: Special = MonthlyCTC - (All other components + Employer logic)
      const rem =
        monthlyCTC -
        basic -
        da -
        hra -
        transport -
        medical -
        other -
        bonus -
        lta -
        pfEmployerVal -
        esiEmployerVal -
        gratuityVal;
      // Note: In reactive effect, if rem is negative, we set Special to 0.
      // Manual "Apply" handles scaling; reactive effect just balances.
      const expectedSpecial = rem > 0 ? rem.toFixed(2) : "0.00";

      if (
        parseFloat(formData.special_allowance || 0) !==
        parseFloat(expectedSpecial)
      ) {
        updates.special_allowance = expectedSpecial;
        shouldUpdate = true;
      }
    }

    if (shouldUpdate) {
      setFormData((prev) => ({ ...prev, ...updates }));
    }
  }, [
    formData.pf_enabled,
    formData.esi_enabled,
    formData.basic_salary,
    formData.dearness_allowance,
    formData.ctc,
    formData.hra,
    formData.transport_allowance,
    formData.medical_allowance,
    formData.other_allowances,
    formData.bonus,
    formData.lta,
    formData.gratuity,
    formData.esi_employer,
    formData.esi_employee,
  ]);

  // Debounced search for reporting to
  useEffect(() => {
    if (reportingToSearchTimeoutRef.current) {
      clearTimeout(reportingToSearchTimeoutRef.current);
    }

    if (reportingToSearchQuery.trim().length > 0) {
      setIsSearchingReportingTo(true);
      reportingToSearchTimeoutRef.current = setTimeout(async () => {
        try {
          const response = await interviewService.searchInterviewers(
            reportingToSearchQuery,
          );
          if (response.success && response.data) {
            const formattedResults = response.data.map((interviewer) => ({
              ...interviewer,
              employee_id:
                interviewer.employee_id ||
                interviewer.id ||
                interviewer.user_id,
              full_name:
                interviewer.full_name ||
                interviewer.name ||
                `${interviewer.first_name || ""} ${interviewer.last_name || ""}`.trim(),
            }));
            setReportingToSearchResults(formattedResults);
            setShowReportingToDropdown(true);
          } else {
            setReportingToSearchResults([]);
          }
        } catch (error) {
          console.error("Error searching reporting to:", error);
          setReportingToSearchResults([]);
        } finally {
          setIsSearchingReportingTo(false);
        }
      }, 400); // 400ms debounce
    } else {
      setReportingToSearchResults([]);
      setShowReportingToDropdown(false);
      setIsSearchingReportingTo(false);
    }

    return () => {
      if (reportingToSearchTimeoutRef.current) {
        clearTimeout(reportingToSearchTimeoutRef.current);
      }
    };
  }, [reportingToSearchQuery]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        reportingToDropdownRef.current &&
        !reportingToDropdownRef.current.contains(event.target)
      ) {
        setShowReportingToDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleApplyCTC = () => {
    const annualCTC = parseFloat(formData.ctc) || 0;
    if (annualCTC <= 0) return;

    if (salaryConfigurations) {
      const dynamicResult = calculateDynamicSalary(
        annualCTC,
        salaryConfigurations,
        {
          pf_enabled: formData.pf_enabled,
          esi_enabled: formData.esi_enabled,
          gender: formData.gender,
        },
      );

      if (dynamicResult) {
        // Create a blank slate for salary fields
        const salaryFieldsToClear = {
          basic_salary: "0",
          dearness_allowance: "0",
          hra: "0",
          transport_allowance: "0",
          medical_allowance: "0",
          special_allowance: "0",
          other_allowances: "0",
          bonus: "0",
          lta: "0",
          pf_employee: "0",
          pf_employer: "0",
          esi_employee: "0",
          esi_employer: "0",
          professional_tax: "0",
          income_tax: "0",
          gratuity: "0",
        };

        // Also clear any dynamic components from config
        salaryConfigurations.salary_components.forEach((c) => {
          salaryFieldsToClear[c.code] = "0";
        });

        const newSalaryState = { ...salaryFieldsToClear };

        // Fill from dynamic results
        Object.entries(dynamicResult.earnings || {}).forEach(([k, v]) => {
          newSalaryState[k] = v.toFixed(2);
        });
        Object.entries(dynamicResult.deductions || {}).forEach(([k, v]) => {
          newSalaryState[k] = v.toFixed(2);
        });
        Object.entries(dynamicResult.contributions || {}).forEach(([k, v]) => {
          newSalaryState[k] = v.toFixed(2);
        });

        // Compatibility mapping: Ensure standard legacy fields are set if a dynamic equivalent exists
        // This ensures that "Basic Salary" field bound to 'basic_salary' shows a value if engine only returns 'basic'
        const legacyMappings = {
          basic_salary: ["basic", "basic_da"],
          hra: ["hr", "hra"],
          transport_allowance: ["coy", "transport"],
          bonus: ["bo", "bonus"],
          special_allowance: ["sp_all", "s_all", "special"],
          professional_tax: ["pt"],
          dearness_allowance: ["da"],
        };

        Object.entries(legacyMappings).forEach(([legacy, dynamics]) => {
          if (newSalaryState[legacy] === "0" || !newSalaryState[legacy]) {
            const foundVal = dynamics.find(
              (d) => dynamicResult.earnings[d] || dynamicResult.deductions[d],
            );
            if (foundVal) {
              newSalaryState[legacy] = (
                dynamicResult.earnings[foundVal] ||
                dynamicResult.deductions[foundVal] ||
                0
              ).toFixed(2);
            }
          }
        });

        setFormData((prev) => ({
          ...prev,
          ...newSalaryState,
        }));
        setSalaryApplied(true);
        return;
      }
    }

    // Fallback to old calculator if no API config
    const summary = calculateSalarySummary(annualCTC, {
      pf_enabled: formData.pf_enabled,
      esi_enabled: formData.esi_enabled,
      gender: formData.gender,
      lta: parseFloat(formData.lta) || 0,
    });

    setFormData((prev) => ({
      ...prev,
      // If above 30k structure maps basic and DA together into basic_da, bind to basic_salary
      basic_salary: summary.basic.toFixed(2),
      dearness_allowance: summary.da.toFixed(2),
      hra: summary.hra.toFixed(2),
      transport_allowance: summary.conveyance.toFixed(2),
      bonus: summary.bonus.toFixed(2),
      lta: summary.lta.toFixed(2),
      special_allowance: summary.special > 0 ? summary.special.toFixed(2) : "0",
      pf_employee: summary.pf_employee.toFixed(2),
      pf_employer: summary.pf_employer.toFixed(2),
      esi_employee: summary.esi_employee.toFixed(2),
      esi_employer: summary.esi_employer.toFixed(2),
      professional_tax: summary.pt.toFixed(2),
      income_tax: summary.it.toFixed(2),
      // Maintain pf_enabled/esi_enabled from user selection
      pf_enabled: formData.pf_enabled,
      esi_enabled: formData.esi_enabled,
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Salary fields that need number validation and length check
    const salaryFields = [
      "ctc",
      "basic_salary",
      "hra",
      "transport_allowance",
      "medical_allowance",
      "special_allowance",
      "other_allowances",
      "pf_employee",
      "pf_employer",
      "esi_employee",
      "esi_employer",
      "professional_tax",
      "dearness_allowance",
      "bonus",
      "gratuity",
      "income_tax",
      "lta",
    ];

    if (
      salaryFields.includes(name) ||
      salaryConfigurations?.salary_components.some((c) => c.code === name)
    ) {
      // Only allow numbers
      const numericValue = value.replace(/[^0-9.]/g, "");

      if (name === "ctc") {
        setFormData((prev) => ({
          ...prev,
          [name]: numericValue,
          pf_enabled: false,
          esi_enabled: false,
        }));
      } else {
        setFormData((prev) => ({ ...prev, [name]: numericValue }));
      }

      if (numericValue.length > 10) {
        setErrors((prev) => ({
          ...prev,
          [name]: "Value cannot exceed 10 digits",
        }));
      } else {
        if (errors[name]) {
          setErrors((prev) => ({ ...prev, [name]: "" }));
        }
      }
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = (tabToValidate = null) => {
    const newErrors = {};
    const checkTab = tabToValidate || activeTab;
    const isFullValidation = !tabToValidate;

    // Personal tab validation
    if (checkTab === "personal" || isFullValidation) {
      if (!formData.first_name.trim())
        newErrors.first_name = "First name is required";
      if (!formData.middle_name.trim())
        newErrors.middle_name = "Middle name is required";
      if (!formData.last_name.trim())
        newErrors.last_name = "Last name is required";
      if (!formData.email.trim()) {
        newErrors.email = "Email is required";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = "Invalid email format";
      }
      if (!formData.phone || !formData.phone.trim())
        newErrors.phone = "Phone is required";
      if (!formData.date_of_birth)
        newErrors.date_of_birth = "Date of birth is required";
      if (!formData.date_of_joining)
        newErrors.date_of_joining = "Date of joining is required";
      if (!formData.department_id)
        newErrors.department_id = "Department is required";
      if (!formData.designation_id)
        newErrors.designation_id = "Designation is required";
      if (!formData.reporting_to || !formData.reporting_to.trim())
        newErrors.reporting_to = "Reporting to is required";
      if (!formData.employment_type_id)
        newErrors.employment_type_id = "Employment type is required";
      if (!formData.gender) newErrors.gender = "Gender is required";
      if (!formData.status) newErrors.status = "Status is required";
    }

    // Salary tab validation
    if (checkTab === "salary" || isFullValidation) {
      if (!formData.basic_salary || parseFloat(formData.basic_salary) <= 0) {
        newErrors.basic_salary = "Basic salary is required";
      }
    }

    setErrors((prev) => ({ ...prev, ...newErrors }));
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Only submit if we're on the last tab AND the submit button was explicitly clicked
    if (activeTab !== "address" || !submitButtonClicked.current) {
      submitButtonClicked.current = false;
      return;
    }

    // Reset the flag
    submitButtonClicked.current = false;

    // Full validation on submit
    if (!validateForm()) {
      // Switch to the tab with errors
      const personalFields = [
        "first_name",
        "middle_name",
        "last_name",
        "email",
        "phone",
        "date_of_birth",
        "date_of_joining",
        "department_id",
        "designation_id",
        "reporting_to",
        "employment_type_id",
        "gender",
        "status",
      ];
      if (Object.keys(errors).some((field) => personalFields.includes(field))) {
        setActiveTab("personal");
      } else if (errors.basic_salary) {
        setActiveTab("salary");
      }
      return;
    }

    // Prepare submission data
    const summary = calculateSalarySummary();
    const submissionData = {
      first_name: formData.first_name,
      middle_name: formData.middle_name || null,
      last_name: formData.last_name,
      email: formData.email,
      phone: formData.phone || null,
      date_of_birth: formData.date_of_birth || null,
      date_of_joining: formData.date_of_joining,
      department_id: formData.department_id,
      designation_id: formData.designation_id,
      reporting_to: formData.reporting_to || null,
      employment_type_id: formData.employment_type_id || null,
      gender: formData.gender || null,
      status: formData.status || "Active",
      // Root Level Salary Flags
      ctc: parseFloat(formData.ctc) || 0,
      net_salary: summary.netSalary || 0,
      gross_salary: summary.grossSalary || 0,
      pf_enabled: !!formData.pf_enabled,
      esi_enabled: !!formData.esi_enabled,
      // Nested Salary Breakdown
      salary_breakdown: {
        basic_salary: parseFloat(formData.basic_salary) || 0,
        hra: parseFloat(formData.hra) || 0,
        transport_allowance: parseFloat(formData.transport_allowance) || 0,
        medical_allowance: parseFloat(formData.medical_allowance) || 0,
        special_allowance: parseFloat(formData.special_allowance) || 0,
        other_allowances: parseFloat(formData.other_allowances) || 0,
        pf_employee: formData.pf_enabled
          ? parseFloat(formData.pf_employee) || 0
          : 0,
        pf_employer: formData.pf_enabled
          ? parseFloat(formData.pf_employer) || 0
          : 0,
        esi_employee: formData.esi_enabled
          ? parseFloat(formData.esi_employee) || 0
          : 0,
        esi_employer: formData.esi_enabled
          ? parseFloat(formData.esi_employer) || 0
          : 0,
        professional_tax: parseFloat(formData.professional_tax) || 0,
        dearness_allowance: parseFloat(formData.dearness_allowance) || 0,
        bonus: parseFloat(formData.bonus) || 0,
        gratuity: parseFloat(formData.gratuity) || 0,
        income_tax: parseFloat(formData.income_tax) || 0,
        lta: parseFloat(formData.lta) || 0,
      },
      // Bank
      account_holder_name: formData.account_holder_name || null,
      account_number: formData.account_number || null,
      bank_name: formData.bank_name || null,
      ifsc_code: formData.ifsc_code || null,
      branch_name: formData.branch_name || null,
      account_type: formData.account_type || null,
      // Statutory
      pan_number: formData.pan_number || null,
      aadhar_number: formData.aadhar_number || null,
      uan_number: formData.uan_number || null,
      esi_number: formData.esi_number || null,
      // Address
      current_address: formData.current_address || null,
      permanent_address: formData.permanent_address || null,
      city: formData.city || null,
      state: formData.state || null,
      pin_code: formData.pin_code || null,
    };

    // Dynamically append any extra salary components to the breakdown
    if (salaryConfigurations && salaryConfigurations.salary_components) {
      salaryConfigurations.salary_components.forEach((comp) => {
        if (submissionData.salary_breakdown[comp.code] === undefined) {
          submissionData.salary_breakdown[comp.code] =
            parseFloat(formData[comp.code]) || 0;
        }
      });
    }

    setServerError(null);
    try {
      await onSubmit(submissionData);
    } catch (error) {
      console.error("Form submission error:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to save employee. Please check the form for errors.";
      setServerError(errorMessage);

      // If there are validation errors from the server, map them
      if (error.response?.data?.errors) {
        const serverValidationErrors = {};
        Object.entries(error.response.data.errors).forEach(([key, value]) => {
          serverValidationErrors[key] = Array.isArray(value) ? value[0] : value;
        });
        setErrors((prev) => ({ ...prev, ...serverValidationErrors }));

        // Auto-switch to the first tab that has an error
        const errorFields = Object.keys(serverValidationErrors);
        const personalFields = [
          "first_name",
          "last_name",
          "email",
          "phone",
          "date_of_birth",
          "date_of_joining",
          "department_id",
          "designation_id",
          "reporting_to",
          "employment_type_id",
          "gender",
          "status",
        ];
        const salaryFields = [
          "ctc",
          "basic_salary",
          "hra",
          "transport_allowance",
          "medical_allowance",
          "special_allowance",
          "other_allowances",
          "pf_employee",
          "pf_employer",
          "esi_employee",
          "esi_employer",
          "professional_tax",
        ];

        if (errorFields.some((field) => personalFields.includes(field))) {
          setActiveTab("personal");
        } else if (errorFields.some((field) => salaryFields.includes(field))) {
          setActiveTab("salary");
        }
      }
    }
  };

  const tabs = [
    { id: "personal", label: "Personal", icon: User },
    { id: "salary", label: "Salary", icon: IndianRupee },
    { id: "bank", label: "Bank", icon: Building2 },
    { id: "statutory", label: "Statutory", icon: FileText },
    { id: "address", label: "Address", icon: MapPin },
  ];

  const renderPersonalTab = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
        Personal Information
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            First Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="first_name"
            value={formData.first_name}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white ${
              errors.first_name ? "border-red-500" : "border-gray-300"
            }`}
          />
          {errors.first_name && (
            <p className="text-red-500 text-xs mt-1">{errors.first_name}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Middle Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="middle_name"
            value={formData.middle_name}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white ${
              errors.middle_name ? "border-red-500" : "border-gray-300"
            }`}
          />
          {errors.middle_name && (
            <p className="text-red-500 text-xs mt-1">{errors.middle_name}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Last Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="last_name"
            value={formData.last_name}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white ${
              errors.last_name ? "border-red-500" : "border-gray-300"
            }`}
          />
          {errors.last_name && (
            <p className="text-red-500 text-xs mt-1">{errors.last_name}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Email <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white ${
              errors.email ? "border-red-500" : "border-gray-300"
            }`}
          />
          {errors.email && (
            <p className="text-red-500 text-xs mt-1">{errors.email}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Phone <span className="text-red-500">*</span>
          </label>
          <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white ${
              errors.phone ? "border-red-500" : "border-gray-300"
            }`}
          />
          {errors.phone && (
            <p className="text-red-500 text-xs mt-1">{errors.phone}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Date of Birth <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            name="date_of_birth"
            value={formData.date_of_birth}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white ${
              errors.date_of_birth ? "border-red-500" : "border-gray-300"
            }`}
          />
          {errors.date_of_birth && (
            <p className="text-red-500 text-xs mt-1">{errors.date_of_birth}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Date of Joining <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            name="date_of_joining"
            value={formData.date_of_joining}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white ${
              errors.date_of_joining ? "border-red-500" : "border-gray-300"
            }`}
          />
          {errors.date_of_joining && (
            <p className="text-red-500 text-xs mt-1">
              {errors.date_of_joining}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Department <span className="text-red-500">*</span>
          </label>
          <select
            name="department_id"
            value={formData.department_id}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white ${
              errors.department_id ? "border-red-500" : "border-gray-300"
            }`}
          >
            <option value="">Select department</option>
            {departments.map((dept) => (
              <option key={dept.id} value={dept.id}>
                {dept.name}
              </option>
            ))}
          </select>
          {errors.department_id && (
            <p className="text-red-500 text-xs mt-1">{errors.department_id}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Designation <span className="text-red-500">*</span>
          </label>
          <select
            name="designation_id"
            value={formData.designation_id}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white ${
              errors.designation_id ? "border-red-500" : "border-gray-300"
            }`}
          >
            <option value="">Select designation</option>
            {designations.map((desg) => (
              <option key={desg.id} value={desg.id}>
                {desg.name}
              </option>
            ))}
          </select>
          {errors.designation_id && (
            <p className="text-red-500 text-xs mt-1">{errors.designation_id}</p>
          )}
        </div>

        <div className="relative" ref={reportingToDropdownRef}>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Reporting To <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="reporting_to"
            value={reportingToSearchQuery}
            onChange={(e) => {
              const value = e.target.value;
              setReportingToSearchQuery(value);
              // Only clear the actual reporting_to ID if the search query is emptied
              if (!value) {
                setFormData((prev) => ({ ...prev, reporting_to: "" }));
              }
            }}
            onFocus={() => {
              if (
                reportingToSearchQuery ||
                reportingToSearchResults.length > 0
              ) {
                setShowReportingToDropdown(true);
              }
            }}
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white ${
              errors.reporting_to ? "border-red-500" : "border-gray-300"
            }`}
            placeholder="Search by name or employee ID"
          />
          {errors.reporting_to && (
            <p className="text-red-500 text-xs mt-1">{errors.reporting_to}</p>
          )}

          {/* Search Results Dropdown */}
          {showReportingToDropdown && (
            <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg shadow-lg max-h-60 overflow-auto">
              {isSearchingReportingTo ? (
                <div className="p-3 text-center text-gray-500 dark:text-gray-400">
                  Searching...
                </div>
              ) : reportingToSearchResults.length > 0 ? (
                <ul className="py-1">
                  {reportingToSearchResults.map((person) => (
                    <li
                      key={person.employee_id}
                      onClick={() => {
                        const targetId =
                          person.employee_id || person.id || person.user_id;
                        setFormData((prev) => ({
                          ...prev,
                          reporting_to: String(targetId),
                        }));
                        setReportingToSearchQuery(person.full_name);
                        setShowReportingToDropdown(false);
                      }}
                      className="px-4 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {person.full_name}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {person.employee_id}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : reportingToSearchQuery.trim().length > 0 ? (
                <div className="p-3 text-center text-gray-500 dark:text-gray-400">
                  No results found
                </div>
              ) : null}
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Employment Type <span className="text-red-500">*</span>
          </label>
          <select
            name="employment_type_id"
            value={formData.employment_type_id}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white ${
              errors.employment_type_id ? "border-red-500" : "border-gray-300"
            }`}
          >
            <option value="">Select employment type</option>
            {employmentTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </select>
          {errors.employment_type_id && (
            <p className="text-red-500 text-xs mt-1">
              {errors.employment_type_id}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Gender <span className="text-red-500">*</span>
          </label>
          <select
            name="gender"
            value={formData.gender}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white ${
              errors.gender ? "border-red-500" : "border-gray-300"
            }`}
          >
            <option value="">Select gender</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="Other">Other</option>
          </select>
          {errors.gender && (
            <p className="text-red-500 text-xs mt-1">{errors.gender}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Status <span className="text-red-500">*</span>
          </label>
          <select
            name="status"
            value={formData.status}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white ${
              errors.status ? "border-red-500" : "border-gray-300"
            }`}
          >
            <option value="">Select status</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
          {errors.status && (
            <p className="text-red-500 text-xs mt-1">{errors.status}</p>
          )}
        </div>
      </div>
    </div>
  );

  const renderSalaryTab = () => {
    const isEditMode = !!(
      initialValues?.employee_id ||
      initialValues?.employment_id ||
      initialValues?.id
    );
    const showOnlyResponseFields =
      isEditMode && !salaryApplied && initialValues?.salary_breakdown;

    return (
      <div className="space-y-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Cost to Company (CTC)
            </h3>
            <p className="text-sm text-gray-500">
              Auto-structure salary components based on annual CTC
            </p>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">
                ₹
              </span>
              <input
                type="text"
                name="ctc"
                placeholder="Enter Annual CTC"
                value={formData.ctc}
                onChange={handleChange}
                className={`pl-7 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white text-sm w-64 ${
                  errors.ctc
                    ? "border-red-500 ring-1 ring-red-500"
                    : "border-gray-300 dark:border-gray-700"
                }`}
              />
            </div>
            <button
              type="button"
              onClick={handleApplyCTC}
              className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors"
            >
              Apply
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {salaryConfigurations && !showOnlyResponseFields ? (
                // Dynamic Earnings from Config filtered by current architecture
                <>
                  {salaryConfigurations.salary_components
                    .filter(
                      (c) =>
                        c.type === "earning" &&
                        c.calc !== "balancing" &&
                        c.structure_id ===
                          ((parseFloat(formData.ctc) || 0) / 12 < 30000
                            ? "below_30k"
                            : "above_30k"),
                    )
                    .map((comp) => (
                      <div key={comp.id}>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          {comp.name}
                        </label>
                        <input
                          type="text"
                          name={comp.code}
                          value={formData[comp.code] || "0"}
                          onChange={handleChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
                        />
                      </div>
                    ))}
                  {/* 
                  Extra Protection for Edit Mode: 
                  If we are in Edit mode, show any OTHER fields from the response 
                  that were in the salary_breakdown but not represented above.
                */}
                  {initialValues?.salary_breakdown &&
                    Object.entries(initialValues.salary_breakdown)
                      .filter(([key]) => {
                        // Skip if already rendered by config
                        const isRendered =
                          salaryConfigurations.salary_components.some(
                            (c) => c.code === key,
                          );
                        // Skip standard statutory ones handled at bottom
                        const isStatutory = [
                          "pf_employee",
                          "pf_employer",
                          "esi_employee",
                          "esi_employer",
                          "professional_tax",
                          "income_tax",
                          "gratuity",
                          "ctc",
                          "pf_enabled",
                          "esi_enabled",
                        ].includes(key);
                        // Only show if value exists and not hidden
                        return !isRendered && !isStatutory;
                      })
                      .map(([key, value]) => (
                        <div key={key}>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            {key
                              .split("_")
                              .map(
                                (w) => w.charAt(0).toUpperCase() + w.slice(1),
                              )
                              .join(" ")}
                          </label>
                          <input
                            type="text"
                            name={key}
                            value={formData[key] || "0"}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
                          />
                        </div>
                      ))}
                </>
              ) : initialValues?.salary_breakdown ? (
                // Initially in Edit Mode, ONLY show those fields that are present in the response
                Object.entries(initialValues.salary_breakdown)
                  .filter(
                    ([key]) =>
                      ![
                        "pf_employee",
                        "pf_employer",
                        "esi_employee",
                        "esi_employer",
                        "professional_tax",
                        "income_tax",
                        "gratuity",
                        "ctc",
                        "pf_enabled",
                        "esi_enabled",
                      ].includes(key),
                  )
                  .map(([key, value]) => (
                    <div key={key}>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {key
                          .split("_")
                          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                          .join(" ")}
                      </label>
                      <input
                        type="text"
                        name={key}
                        value={formData[key] || "0"}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
                      />
                    </div>
                  ))
              ) : (
                // Simple Minimal Fallback UI for new employees if config fails
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Basic Salary
                    </label>
                    <input
                      type="text"
                      name="basic_salary"
                      value={formData.basic_salary}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      HRA
                    </label>
                    <input
                      type="text"
                      name="hra"
                      value={formData.hra}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
                    />
                  </div>
                </>
              )}

              {/* Always show balancing field if configuration exists */}
              {salaryConfigurations?.salary_components
                .filter(
                  (c) =>
                    c.calc === "balancing" &&
                    c.structure_id ===
                      ((parseFloat(formData.ctc) || 0) / 12 < 30000
                        ? "below_30k"
                        : "above_30k"),
                )
                .map((comp) => (
                  <div key={comp.id}>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {comp.name}
                    </label>
                    <input
                      type="text"
                      name={comp.code}
                      value={formData[comp.code] || "0"}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 bg-gray-50 dark:bg-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-white"
                    />
                  </div>
                ))}

              {/* Statutory inputs - only show if not already rendered or if needed */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Monthly Income Tax
                </label>
                <input
                  type="text"
                  name="income_tax"
                  value={formData.income_tax}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Gratuity
                </label>
                <input
                  type="text"
                  name="gratuity"
                  value={formData.gratuity}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
                />
              </div>
            </div>

            <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
              <div className="flex items-center gap-6 flex-wrap mb-4">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!formData.pf_enabled}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        pf_enabled: e.target.checked,
                      }))
                    }
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  PF Enabled
                </label>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!formData.esi_enabled}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        esi_enabled: e.target.checked,
                      }))
                    }
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  ESIC Enabled
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {formData.pf_enabled && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        PF Employee
                      </label>
                      <input
                        type="text"
                        name="pf_employee"
                        value={formData.pf_employee}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        PF Employer
                      </label>
                      <input
                        type="text"
                        name="pf_employer"
                        value={formData.pf_employer}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
                      />
                    </div>
                  </>
                )}
                {formData.esi_enabled && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        ESIC Employee
                      </label>
                      <input
                        type="text"
                        name="esi_employee"
                        value={formData.esi_employee}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        ESIC Employer
                      </label>
                      <input
                        type="text"
                        name="esi_employer"
                        value={formData.esi_employer}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
                      />
                    </div>
                  </>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Professional Tax
                  </label>
                  <input
                    type="text"
                    name="professional_tax"
                    value={formData.professional_tax}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Right: Salary summary */}
          <div className="lg:col-span-1">
            <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-xl h-fit border border-gray-200 dark:border-gray-700">
              <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-4">
                Salary Summary
              </h4>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Employee Gross
                  </p>
                  <p className="text-lg font-semibold text-gray-900 dark:text-white">
                    ₹
                    {Math.trunc(salarySummary.grossSalary).toLocaleString(
                      "en-IN",
                    )}
                  </p>
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Total Deductions
                  </p>
                  <p className="text-lg font-semibold text-red-600">
                    -₹
                    {Math.trunc(salarySummary.totalDeductions).toLocaleString(
                      "en-IN",
                    )}
                  </p>
                </div>
                <div className="border-t border-gray-200 dark:border-gray-700" />
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    Net In Hand
                  </p>
                  <p className="text-xl font-bold text-green-600">
                    ₹
                    {Math.trunc(salarySummary.netSalary).toLocaleString(
                      "en-IN",
                    )}
                  </p>
                </div>
                <div className="border-t border-gray-200 dark:border-gray-700" />
                <div className="space-y-2">
                  <p className="text-xs font-semibold text-gray-500 uppercase">
                    Employer Contributions
                  </p>
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-gray-600">Total</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      ₹
                      {Math.trunc(
                        salarySummary.employerContributions,
                      ).toLocaleString("en-IN")}
                    </p>
                  </div>
                </div>
                <div className="border-t border-gray-200 dark:border-gray-700" />
                <div className="flex items-center justify-between bg-blue-50 dark:bg-blue-900/20 p-2 rounded">
                  <p className="text-sm font-bold text-blue-700 dark:text-blue-400">
                    Monthly CTC
                  </p>
                  <p className="text-lg font-bold text-blue-700 dark:text-blue-400">
                    ₹
                    {Math.trunc(salarySummary.monthlyCTC).toLocaleString(
                      "en-IN",
                    )}
                  </p>
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Annual CTC
                  </p>
                  <p className="text-lg font-semibold text-gray-900 dark:text-white">
                    ₹
                    {Math.trunc(salarySummary.annualCTC).toLocaleString(
                      "en-IN",
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderBankTab = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
        Bank Account Details
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Account Holder Name
          </label>
          <input
            type="text"
            name="account_holder_name"
            value={formData.account_holder_name}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Account Number
          </label>
          <input
            type="text"
            name="account_number"
            value={formData.account_number}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Bank Name
          </label>
          <input
            type="text"
            name="bank_name"
            value={formData.bank_name}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            IFSC Code
          </label>
          <input
            type="text"
            name="ifsc_code"
            value={formData.ifsc_code}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Branch Name
          </label>
          <input
            type="text"
            name="branch_name"
            value={formData.branch_name}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Account Type
          </label>
          <select
            name="account_type"
            value={formData.account_type}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
          >
            <option value="Savings">Savings</option>
            <option value="Current">Current</option>
          </select>
        </div>
      </div>
    </div>
  );

  const renderStatutoryTab = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
        Statutory Information
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            PAN Number
          </label>
          <input
            type="text"
            name="pan_number"
            value={formData.pan_number}
            onChange={handleChange}
            maxLength={10}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Aadhar Number
          </label>
          <input
            type="text"
            name="aadhar_number"
            value={formData.aadhar_number}
            onChange={handleChange}
            maxLength={12}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            UAN Number
          </label>
          <input
            type="text"
            name="uan_number"
            value={formData.uan_number}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            ESIC Number
          </label>
          <input
            type="text"
            name="esi_number"
            value={formData.esi_number}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
          />
        </div>
      </div>
    </div>
  );

  const renderAddressTab = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
        Address Information
      </h3>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Current Address
          </label>
          <textarea
            name="current_address"
            value={formData.current_address}
            onChange={handleChange}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Permanent Address
          </label>
          <textarea
            name="permanent_address"
            value={formData.permanent_address}
            onChange={handleChange}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              City
            </label>
            <input
              type="text"
              name="city"
              value={formData.city}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              State
            </label>
            <input
              type="text"
              name="state"
              value={formData.state}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              PIN Code
            </label>
            <input
              type="text"
              name="pin_code"
              value={formData.pin_code}
              onChange={handleChange}
              maxLength={6}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
            />
          </div>
        </div>
      </div>
    </div>
  );

  // Prevent form submission on Enter key press in input fields
  // Only allow submission when submit button is explicitly clicked
  const handleFormKeyDown = (e) => {
    if (e.key === "Enter") {
      const target = e.target;
      // Prevent Enter from submitting form unless it's on the submit button
      if (target.tagName !== "BUTTON" || target.type !== "submit") {
        e.preventDefault();
        e.stopPropagation();
        // If not on last tab, navigate to next tab on Enter
        if (activeTab !== "address") {
          // Validate current tab before moving
          if (validateForm(activeTab)) {
            const currentIndex = tabs.findIndex((tab) => tab.id === activeTab);
            if (currentIndex < tabs.length - 1) {
              setActiveTab(tabs[currentIndex + 1].id);
            }
          }
        }
      }
    }
  };

  // Handle submit button click
  const handleSubmitButtonClick = async (e) => {
    e.preventDefault();
    submitButtonClicked.current = true;
    // Create a synthetic event to pass to handleSubmit
    const syntheticEvent = {
      preventDefault: () => {},
      target: e.target,
    };
    await handleSubmit(syntheticEvent);
  };

  return (
    <form
      onSubmit={handleSubmit}
      onKeyDown={handleFormKeyDown}
      className="space-y-6"
    >
      {/* Server Error Alert */}
      {serverError && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start gap-3 animate-in fade-in slide-in-from-top-2">
          <AlertCircle
            className="text-red-600 dark:text-red-400 shrink-0 mt-0.5"
            size={18}
          />
          <div>
            <h4 className="text-sm font-semibold text-red-800 dark:text-red-300">
              Submission Failed
            </h4>
            <p className="text-sm text-red-700 dark:text-red-400 mt-1">
              {serverError}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setServerError(null)}
            className="ml-auto text-red-500 hover:text-red-700 transition-colors"
          >
            <X size={18} />
          </button>
        </div>
      )}

      {/* Tabs Navigation */}
      <div className="flex justify-between items-center border-b border-gray-200 dark:border-gray-700">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => {
                const targetIndex = tabs.findIndex((t) => t.id === tab.id);
                const currentIndex = tabs.findIndex((t) => t.id === activeTab);

                if (targetIndex > currentIndex) {
                  // Only allow forward navigation if current tab is valid
                  if (validateForm(activeTab)) {
                    setActiveTab(tab.id);
                  }
                } else {
                  // Always allow going back
                  setActiveTab(tab.id);
                  // Clear server errors when switching tabs
                  setServerError(null);
                }
              }}
              className={`flex items-center gap-2 px-5 py-3 font-medium text-sm transition-colors ${
                isActive
                  ? "border-b-2 border-blue-500 text-blue-600 dark:text-blue-400"
                  : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
              }`}
            >
              <Icon size={18} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div className="min-h-[400px]">
        {activeTab === "personal" && renderPersonalTab()}
        {activeTab === "salary" && renderSalaryTab()}
        {activeTab === "bank" && renderBankTab()}
        {activeTab === "statutory" && renderStatutoryTab()}
        {activeTab === "address" && renderAddressTab()}
      </div>

      {/* Form Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
        {activeTab !== "personal" && (
          <button
            type="button"
            onClick={() => {
              const currentIndex = tabs.findIndex(
                (tab) => tab.id === activeTab,
              );
              if (currentIndex > 0) {
                setActiveTab(tabs[currentIndex - 1].id);
              }
            }}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            <ChevronLeft size={18} />
            Back
          </button>
        )}

        {activeTab !== "address" ? (
          <button
            type="button"
            onClick={() => {
              // Validate current tab before moving
              if (validateForm(activeTab)) {
                const currentIndex = tabs.findIndex(
                  (tab) => tab.id === activeTab,
                );
                if (currentIndex < tabs.length - 1) {
                  setActiveTab(tabs[currentIndex + 1].id);
                }
              }
            }}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            Next
            <ChevronRight size={18} />
          </button>
        ) : (
          <button
            type="button"
            onClick={handleSubmitButtonClick}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading
              ? "Saving..."
              : initialValues?.employee_id || initialValues?.employment_id
                ? "Update Employee"
                : "Create Employee"}
          </button>
        )}
      </div>
    </form>
  );
};

export default EmployeeForm;
